// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createActionsHook,
	createStateHook,
} from '@atlassian/react-sweet-state';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { EXPERIMENT_KEY, BANNER_HEIGHT, type Banners } from '../../common/constants.tsx';

type BannerState = {
	isDismissed: boolean;
	height: number;
	isLoading: boolean;
};

export type State = {
	software: BannerState;
	service_desk: BannerState;
};

export type LocalStorage = {
	software: BannerDismissedStates;
	service_desk: BannerDismissedStates;
};

export type BannerDismissedStates = {
	month: number;
	'80_90': boolean;
	'90_100': boolean;
	gte100: boolean;
};

export const initialState: State = {
	software: { isDismissed: false, height: 0, isLoading: false },
	service_desk: { isDismissed: false, height: 0, isLoading: false },
};

export const createUpsellAutomationLimitAwarenessLocalStorage = (prefix: string) =>
	createLocalStorageProvider(`${prefix}-${EXPERIMENT_KEY}`);

const getCurrentMonth = () => new Date().getMonth();

export const bannerDismissedStatesDefault: LocalStorage = {
	software: {
		month: getCurrentMonth(),
		'80_90': false,
		'90_100': false,
		gte100: false,
	},
	service_desk: {
		month: getCurrentMonth(),
		'80_90': false,
		'90_100': false,
		gte100: false,
	},
};

export const actions = {
	dismissBanner:
		(cloudId: string, projectType: string, bannerType: string): Action<State> =>
		async ({ setState }) => {
			if (
				!cloudId ||
				(bannerType !== '80_90' && bannerType !== '90_100' && bannerType !== 'gte100')
			) {
				return;
			}

			try {
				const localStorageProvider = createUpsellAutomationLimitAwarenessLocalStorage(cloudId);
				const currentData = localStorageProvider.get(EXPERIMENT_KEY) || {
					...bannerDismissedStatesDefault,
				};

				currentData[projectType][bannerType] = true;
				localStorageProvider.set(EXPERIMENT_KEY, currentData);
				setState({
					[projectType]: {
						isDismissed: true,
						height: 0,
					},
				});
			} catch (error) {
				setState({
					[projectType]: {
						isDismissed: true,
						height: 0,
					},
				});
			}
		},

	checkDismissalState:
		(cloudId: string, projectType: string, bannerType: Banners | undefined): Action<State> =>
		async ({ setState }) => {
			setState({
				[projectType]: {
					isLoading: true,
				},
			});

			if (
				!cloudId ||
				!bannerType ||
				(projectType !== 'software' && projectType !== 'service_desk')
			) {
				setState({
					[projectType]: {
						isDismissed: true,
						isLoading: false,
						height: 0,
					},
				});
				return;
			}
			try {
				const localStorageProvider = createUpsellAutomationLimitAwarenessLocalStorage(cloudId);
				const localStorageData = localStorageProvider.get(EXPERIMENT_KEY);

				let productBannerDismissedStates: BannerDismissedStates;

				// reset state if it doesn't exist or it's expired
				if (!localStorageData || localStorageData[projectType]?.month !== getCurrentMonth()) {
					productBannerDismissedStates = bannerDismissedStatesDefault[projectType];
					localStorageProvider.set(EXPERIMENT_KEY, {
						...bannerDismissedStatesDefault,
					});
				} else {
					productBannerDismissedStates = localStorageData[projectType];
				}

				const isDismissed = productBannerDismissedStates[bannerType] === true;
				setState({
					[projectType]: {
						isDismissed,
						isLoading: false,
						height: isDismissed ? 0 : BANNER_HEIGHT,
					},
				});
			} catch (error) {
				setState({
					[projectType]: {
						isDismissed: false,
						isLoading: false,
						height: 0,
					},
				});
			}
		},
} as const;

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	name: 'AutomationLimitAwarenessBanner',
	initialState,
	actions,
});

export const useAutomationLimitBannerState = createStateHook(Store);
export const useAutomationLimitBannerActions = createActionsHook(Store);
